import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { employeeService } from '../../services/employeeService';
import { useAuth } from '../../context/AuthContext';

// Validation schema
const schema = yup.object().shape({
  // User fields
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().when('isEdit', {
    is: false,
    then: (schema) => schema
      .min(6, 'Password must be at least 6 characters')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number')
      .required('Password is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  firstName: yup.string().min(2, 'Minimum 2 characters').max(50, 'Maximum 50 characters').required('First name is required'),
  fatherName: yup.string().min(2, 'Minimum 2 characters').max(50, 'Maximum 50 characters').required('Father name is required'),
  grandfatherName: yup.string().max(50, 'Maximum 50 characters'),
  lastName: yup.string().min(2, 'Minimum 2 characters').max(50, 'Maximum 50 characters').required('Last name is required'),
  phone: yup.string(),
  roleId: yup.number().positive('Please select a role').required('Role is required'),
  
  // Employee fields
  employeeNumber: yup.string().min(1, 'Employee number is required').max(20, 'Maximum 20 characters').required('Employee number is required'),
  nationalId: yup.string()
    .matches(/^\d{12}$/, 'National ID must be exactly 12 digits')
    .required('National ID is required')
    .test('gender-match', 'National ID must match gender (1=Male, 2=Female)', function(value) {
      if (!value) return true;
      const firstDigit = value.charAt(0);
      const gender = this.parent.gender;
      
      if (firstDigit === '1' && gender !== 'male') {
        return this.createError({ message: 'National ID starting with 1 indicates male gender' });
      }
      if (firstDigit === '2' && gender !== 'female') {
        return this.createError({ message: 'National ID starting with 2 indicates female gender' });
      }
      if (firstDigit !== '1' && firstDigit !== '2') {
        return this.createError({ message: 'National ID must start with 1 (male) or 2 (female)' });
      }
      return true;
    }),
  departmentId: yup.number().positive('Please select a department'),
  jobTitle: yup.string().max(100, 'Maximum 100 characters'),
  employmentType: yup.string().oneOf(['full_time', 'part_time', 'contract', 'intern', 'consultant'], 'Invalid employment type'),
  workLocation: yup.string().oneOf(['office', 'remote', 'hybrid'], 'Invalid work location'),
  dateOfBirth: yup.date(),
  gender: yup.string().oneOf(['male', 'female', 'other'], 'Invalid gender').required('Gender is required'),
  maritalStatus: yup.string().oneOf(['single', 'married', 'divorced', 'widowed'], 'Invalid marital status'),
  joinDate: yup.date(),
  basicSalary: yup.number().min(0, 'Salary must be positive'),
  workingHoursPerDay: yup.number().min(1, 'Minimum 1 hour').max(24, 'Maximum 24 hours'),
  workingDaysPerWeek: yup.number().min(1, 'Minimum 1 day').max(7, 'Maximum 7 days')
});

const EmployeeForm = ({ employee = null, onClose, onSave }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [departments] = useState([
    { id: 1, name: 'Information Technology' },
    { id: 2, name: 'Human Resources' },
    { id: 3, name: 'Finance' },
    { id: 4, name: 'Marketing' },
    { id: 5, name: 'Operations' }
  ]);
  const [roles] = useState([
    { id: 1, name: 'employee', displayName: 'Employee' },
    { id: 2, name: 'manager', displayName: 'Manager' },
    { id: 3, name: 'hr', displayName: 'HR' },
    { id: 4, name: 'admin', displayName: 'Admin' }
  ]);

  const isEdit = !!employee;
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit },
    defaultValues: {
      employmentType: 'full_time',
      workLocation: 'office',
      gender: 'male',
      maritalStatus: 'single',
      currency: 'USD',
      workingHoursPerDay: 8,
      workingDaysPerWeek: 5,
      probationPeriodMonths: 3,
      ...employee && {
        // User fields
        email: employee.User?.email,
        firstName: employee.User?.firstName,
        fatherName: employee.User?.fatherName,
        grandfatherName: employee.User?.grandfatherName,
        lastName: employee.User?.lastName,
        phone: employee.User?.phone,
        roleId: employee.User?.roleId,
        
        // Employee fields
        employeeNumber: employee.employeeNumber,
        departmentId: employee.departmentId,
        jobTitle: employee.jobTitle,
        employmentType: employee.employmentType,
        workLocation: employee.workLocation,
        nationalId: employee.nationalId,
        passportNumber: employee.passportNumber,
        dateOfBirth: employee.dateOfBirth ? new Date(employee.dateOfBirth).toISOString().split('T')[0] : '',
        gender: employee.gender,
        maritalStatus: employee.maritalStatus,
        nationality: employee.nationality,
        personalEmail: employee.personalEmail,
        personalPhone: employee.personalPhone,
        address: employee.address,
        city: employee.city,
        state: employee.state,
        country: employee.country,
        postalCode: employee.postalCode,
        emergencyContactName: employee.emergencyContactName,
        emergencyContactRelation: employee.emergencyContactRelation,
        emergencyContactPhone: employee.emergencyContactPhone,
        emergencyContactEmail: employee.emergencyContactEmail,
        joinDate: employee.joinDate ? new Date(employee.joinDate).toISOString().split('T')[0] : '',
        probationPeriodMonths: employee.probationPeriodMonths,
        contractStartDate: employee.contractStartDate ? new Date(employee.contractStartDate).toISOString().split('T')[0] : '',
        contractEndDate: employee.contractEndDate ? new Date(employee.contractEndDate).toISOString().split('T')[0] : '',
        basicSalary: employee.basicSalary,
        currency: employee.currency,
        bankName: employee.bankName,
        bankAccountNumber: employee.bankAccountNumber,
        bankRoutingNumber: employee.bankRoutingNumber,
        workingHoursPerDay: employee.workingHoursPerDay,
        workingDaysPerWeek: employee.workingDaysPerWeek,
        notes: employee.notes
      }
    }
  });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setValidationErrors({}); // Clear previous errors
      console.log('🔄 Submitting employee data:', data);

      let response;
      if (isEdit) {
        response = await employeeService.updateEmployee(employee.id, data);
        toast.success('Employee updated successfully!');
      } else {
        response = await employeeService.createEmployee(data);
        toast.success('Employee created successfully!');
      }

      if (onSave) {
        onSave(response.data.employee);
      }
      onClose();
    } catch (error) {
      console.error('❌ Employee form error:', error);
      
      // Handle validation errors specifically
      if (error.message === 'Validation failed' && error.response?.data?.details) {
        const errors = {};
        error.response.data.details.forEach(detail => {
          errors[detail.path] = detail.msg;
        });
        setValidationErrors(errors);
        toast.error('Please fix the validation errors highlighted below');
      } else {
        toast.error(error.message || 'Failed to save employee');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    setValidationErrors({});
    onClose();
  };

  // Helper function to get error for a field (from react-hook-form or server validation)
  const getFieldError = (fieldName) => {
    return errors[fieldName]?.message || validationErrors[fieldName];
  };

  // Helper function to determine if field has error
  const hasFieldError = (fieldName) => {
    return !!(errors[fieldName] || validationErrors[fieldName]);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border max-w-4xl shadow-lg rounded-md bg-white mb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
        >
          {/* Header */}
          <div className="flex justify-between items-center pb-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <UserIcon className="h-6 w-6 mr-2 text-blue-600" />
              {isEdit ? 'Edit Employee' : 'Add New Employee'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Show validation errors summary if any */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Please fix the following errors:
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc list-inside space-y-1">
                      {Object.entries(validationErrors).map(([field, message]) => (
                        <li key={field}>
                          <span className="font-medium capitalize">{field.replace(/([A-Z])/g, ' $1')}</span>: {message}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
            <div className="space-y-8">
              {/* Basic Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-gray-600" />
                  Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      {...register('firstName')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('firstName') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ahmed"
                    />
                    {getFieldError('firstName') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('firstName')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Father Name *
                    </label>
                    <input
                      type="text"
                      {...register('fatherName')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('fatherName') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Mohamed"
                    />
                    {getFieldError('fatherName') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('fatherName')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grandfather Name
                    </label>
                    <input
                      type="text"
                      {...register('grandfatherName')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('grandfatherName') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ali"
                    />
                    {getFieldError('grandfatherName') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('grandfatherName')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      {...register('lastName')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('lastName') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Al-Rashid"
                    />
                    {getFieldError('lastName') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('lastName')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('email') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="ahmed.mohamed@company.com"
                    />
                    {getFieldError('email') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('email')}</p>
                    )}
                  </div>

                  {!isEdit && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password *
                      </label>
                      <input
                        type="password"
                        {...register('password')}
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          hasFieldError('password') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Strong password"
                      />
                      {getFieldError('password') && (
                        <p className="mt-1 text-xs text-red-600">{getFieldError('password')}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('phone') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+1234567890"
                    />
                    {getFieldError('phone') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('phone')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      National ID *
                    </label>
                    <input
                      type="text"
                      {...register('nationalId')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('nationalId') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="123456789012"
                      maxLength="12"
                    />
                    {getFieldError('nationalId') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('nationalId')}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      12 digits: Start with 1 for Male, 2 for Female
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      {...register('dateOfBirth')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('dateOfBirth') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    />
                    {getFieldError('dateOfBirth') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('dateOfBirth')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender *
                    </label>
                    <select
                      {...register('gender')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('gender') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {getFieldError('gender') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('gender')}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Must match National ID (1=Male, 2=Female)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Marital Status
                    </label>
                    <select
                      {...register('maritalStatus')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('maritalStatus') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                    {getFieldError('maritalStatus') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('maritalStatus')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Employment Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 mr-2 text-gray-600" />
                  Employment Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee Number *
                    </label>
                    <input
                      type="text"
                      {...register('employeeNumber')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('employeeNumber') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="EMP001"
                      style={{ textTransform: 'uppercase' }}
                    />
                    {getFieldError('employeeNumber') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('employeeNumber')}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Unique employee identifier (e.g., EMP001, DEV-001)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role *
                    </label>
                    <select
                      {...register('roleId')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('roleId') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.displayName}
                        </option>
                      ))}
                    </select>
                    {getFieldError('roleId') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('roleId')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <select
                      {...register('departmentId')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('departmentId') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                    {getFieldError('departmentId') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('departmentId')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Title
                    </label>
                    <input
                      type="text"
                      {...register('jobTitle')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('jobTitle') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Software Developer"
                    />
                    {getFieldError('jobTitle') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('jobTitle')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employment Type
                    </label>
                    <select
                      {...register('employmentType')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('employmentType') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="intern">Intern</option>
                      <option value="consultant">Consultant</option>
                    </select>
                    {getFieldError('employmentType') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('employmentType')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Work Location
                    </label>
                    <select
                      {...register('workLocation')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('workLocation') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="office">Office</option>
                      <option value="remote">Remote</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                    {getFieldError('workLocation') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('workLocation')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Join Date
                    </label>
                    <input
                      type="date"
                      {...register('joinDate')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('joinDate') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                    />
                    {getFieldError('joinDate') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('joinDate')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Basic Salary
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('basicSalary')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('basicSalary') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="50000"
                    />
                    {getFieldError('basicSalary') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('basicSalary')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Working Hours/Day
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      {...register('workingHoursPerDay')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('workingHoursPerDay') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="8"
                    />
                    {getFieldError('workingHoursPerDay') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('workingHoursPerDay')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Working Days/Week
                    </label>
                    <input
                      type="number"
                      {...register('workingDaysPerWeek')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('workingDaysPerWeek') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="5"
                    />
                    {getFieldError('workingDaysPerWeek') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('workingDaysPerWeek')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <PhoneIcon className="h-5 w-5 mr-2 text-gray-600" />
                  Emergency Contact
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      {...register('emergencyContactName')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('emergencyContactName') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Emergency contact name"
                    />
                    {getFieldError('emergencyContactName') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('emergencyContactName')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship
                    </label>
                    <input
                      type="text"
                      {...register('emergencyContactRelation')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('emergencyContactRelation') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Spouse, Parent, etc."
                    />
                    {getFieldError('emergencyContactRelation') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('emergencyContactRelation')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      {...register('emergencyContactPhone')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('emergencyContactPhone') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+1234567890"
                    />
                    {getFieldError('emergencyContactPhone') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('emergencyContactPhone')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      {...register('emergencyContactEmail')}
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        hasFieldError('emergencyContactEmail') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="emergency@example.com"
                    />
                    {getFieldError('emergencyContactEmail') && (
                      <p className="mt-1 text-xs text-red-600">{getFieldError('emergencyContactEmail')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    hasFieldError('notes') ? 'border-red-300 ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Additional notes about the employee..."
                />
                {getFieldError('notes') && (
                  <p className="mt-1 text-xs text-red-600">{getFieldError('notes')}</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-8">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </span>
                ) : (
                  <span>{isEdit ? 'Update Employee' : 'Create Employee'}</span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default EmployeeForm; 